import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import TableComponent from "../../components/table/TableComponent";
import { Column } from "../../interfaces/table.interface";
import { get } from "../../redux/ApiHelper";
import { RootState } from "../../redux/store";
import { PurchasedPackageInterface } from "../../interfaces/purchased-package.interface";
import { formatDateTime } from "../../utils/date&time.util";
import { startLoading, stopLoading } from "../../redux/slices/WindowSlice";

const PurchasedPackageList = () => {
  const [packages, setPackages] = useState<PurchasedPackageInterface[]>([]);
  const [filteredPackages, setFilteredPackages] = useState<
    PurchasedPackageInterface[]
  >([]);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const { signedAdmin } = useSelector((state: RootState) => state.admin);
  const dispatch = useDispatch();

  // Fetch tuition job posts on component mount
  useEffect(() => {
    const queryParams = new URLSearchParams();
    queryParams.set("type", String(signedAdmin.type));
    queryParams.set("adminId", String(signedAdmin.id));
    dispatch(startLoading());
    get(`/admin/parent/purchased-packages-all?${queryParams.toString()}`)
      .then((res) => {
        setPackages(res);
        setFilteredPackages(res);
      })
      .catch((error) => {
        console.log(error);
      }).finally(() => {
        dispatch(stopLoading());
      });
  }, []);

  // Search functionality
  const handleSearch = (query: string) => {
    setSearchQuery(query);
    const lowerQuery = query.toLowerCase();

    const filteredList = packages.filter((pkg) => {
      return (
        pkg.parentId._id?.toLowerCase().includes(lowerQuery) ||
        pkg.parentId.name?.toLowerCase().includes(lowerQuery) ||
        pkg.parentId.email?.toLowerCase().includes(lowerQuery) ||
        pkg.parentId.phoneNumber?.toString().includes(lowerQuery) ||
        pkg.packageId?.toLowerCase().includes(lowerQuery)
      );
    });

    setFilteredPackages(filteredList);
  };

  const columns: Column[] = [
    {
      header: "ParentId",
      render: (_value: any, row: PurchasedPackageInterface) =>
        row?.parentId?._id || "N/A",
    },
    {
      header: "Parent Name",
      render: (_value: any, row: PurchasedPackageInterface) =>
        row?.parentId?.name || "N/A",
    },
    {
      header: "Parent email",
      render: (_value: any, row: PurchasedPackageInterface) =>
        row?.parentId?.email || "N/A",
    },
    {
      select: "parentId",
      header: "ParentId",
      render: (_value: any, row: PurchasedPackageInterface) =>
        row?.parentId?.phoneNumber || "N/A",
    },
    { select: "packageId", header: "packageId" },
    { select: "purchasedCoins", header: "Purchased coins" },
    { select: "availableCoins", header: "Available coins" },
    {
      select: "isExpired",
      header: "Expiry Status",
      render: (_value: any, row: PurchasedPackageInterface) => {
        if (row?.isExpired === true) {
          return (
            <span className="px-2 py-1 text-sm text-red-700 bg-red-100 rounded-full">
              Expired
            </span>
          );
        } else if (row?.isExpired === false) {
          return (
            <span className="px-2 py-1 text-sm text-green-700 bg-green-100 rounded-full">
              Active
            </span>
          );
        } else {
          return (
            <span className="px-2 py-1 text-sm text-gray-700 bg-gray-100 rounded-full">
              ⚪ N/A
            </span>
          );
        }
      },
    },
    {
      select: "validTill",
      header: "Expiry date",
      render: (_value: any, row: PurchasedPackageInterface) => (
        <span className="whitespace-nowrap">
          {formatDateTime(row?.validTill || "")}
        </span>
      ),
    },
    {
      select: "updatedAt",
      header: "Purchased date",
      render: (_value: any, row: PurchasedPackageInterface) => (
        <span className="whitespace-nowrap">
          {formatDateTime(row?.createdAt || "")}
        </span>
      ),
    },
  ];

  return (
    <div className="tuitionJobPosts table-container">
      <div className="title">Parents Purchased packages</div>
      <div className="search-container mb-4">
        <input
          type="text"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-gray-700"
          placeholder="Search by id, parent"
          value={searchQuery}
          onChange={(e) => handleSearch(e.target.value)}
        />
      </div>

      <TableComponent columns={columns} data={filteredPackages} />
    </div>
  );
};

export default PurchasedPackageList;
